import { FaPlay } from 'react-icons/fa6';
import ISiteLink from '../links/ISiteLink';
import { useContext } from 'react';
import styles from './Row.module.css';
import AppContext from '../../AppContext';
import CalculationDetailModal from '../CalculationDetailModal/CalculationDetailModal';
import { RowDataSource } from '../model/RowDataSource';
import RowLinkConfiguration, { RowLinkType } from '../model/RowLinkConfiguration';
import { RowTypeEnum } from './RowTypeEnum';

export interface RowHelmetProps {
  label: string;
  rowKey?: string;
  isOutsideBenchmark?: boolean;
  benchmarkText?: string;
  calculateDetail?: boolean;
  rowType?: RowTypeEnum;
  rowDataSource?: RowDataSource;
  rowLinkConfiguration?: RowLinkConfiguration;
  sectionKey?: string;
  subSectionKey?: string;
  onLabelClick?: () => void;
  isCollapsible?: boolean;
  isExpanded?: boolean;
  forceRollingPrior?: boolean;
}

export default function RowHelmet({
  label,
  rowKey = '0',
  isOutsideBenchmark,
  benchmarkText,
  calculateDetail,
  rowType,
  rowDataSource,
  rowLinkConfiguration,
  sectionKey,
  subSectionKey,
  onLabelClick,
  isCollapsible,
  isExpanded,
  forceRollingPrior
}: RowHelmetProps) {
  const { year, cocode, entityId, statementType, periodType } = useContext(AppContext);
  const isQuarterly = periodType?.startsWith('QUARTER_');

  function multiLineBenchmark(benchmarkText?: string) {
    if (benchmarkText && benchmarkText.includes('OR')) {
      let arr = benchmarkText.split('OR');
      let benchmarkSplitUp = [];
      for (let i = 0; i < arr.length; i++) {
        benchmarkSplitUp.push(
          <span key={i}>
            {arr[i + 1] ? (
              <span>
                {arr[i]}
                <br />
                <span>OR</span>
                <br />
              </span>
            ) : (
              arr[i]
            )}
          </span>
        );
      }
      return benchmarkSplitUp;
    }

    return benchmarkText;
  }

  function renderLabel(label: string, rowLinkConfiguration?: RowLinkConfiguration) {
    switch (rowLinkConfiguration?.rowLinkType) {
      case RowLinkType.PRO_DRILL_DOWN:
        return (
          <ISiteLink
            relativeUrl={`/profile/NAIC.financial.profiles.PRODrillDowns?TAG=${rowLinkConfiguration.tag}&DATA_YEAR=${year}&COCODE=${cocode}`}
            label={label}
          />
        );
      case RowLinkType.MATS:
        return (
          <ISiteLink relativeUrl={`/mats/actionList.faces?entityId=${entityId}`} label={label} />
        );
      case RowLinkType.PICK_A_PAGE:
        return (
          <ISiteLink
            relativeUrl={`/pick-a-page/PickAPage?REPORT=${statementType.statementTypeCode}${year}GENINTPT1INV&COCODE=${statementType.statementTypeCode}${cocode}&DATA_YEAR=${year}`}
            label={label}
          />
        );
      case RowLinkType.EXAM_JUMPSTART:
        return (
          <ISiteLink
            relativeUrl={`/exam-jumpstart/Company?cocode=${cocode}&year=${year}`}
            label={label}
          />
        );

      default:
        return <label>{label}</label>;
    }
  }

  return (
    <>
      <p>
        {isCollapsible ? (
          <label
            onClick={onLabelClick}
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
            role="button"
            aria-expanded={isExpanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLabelClick?.();
              }
            }}
          >
            <span
              style={{
                display: 'inline-block',
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                fontSize: '0.6rem'
              }}
              aria-hidden="true"
            >
              ▶
            </span>
            {renderLabel(label, rowLinkConfiguration)}
          </label>
        ) : (
          renderLabel(label, rowLinkConfiguration)
        )}
      </p>
      <p>
        {calculateDetail ? (
          <CalculationDetailModal
            label={label}
            year={year}
            rowKey={rowKey}
            cocode={cocode}
            rowType={rowType}
            rowDataSource={rowDataSource}
            sectionKey={sectionKey}
            subSectionKey={subSectionKey}
            forceRollingPrior={forceRollingPrior}
          />
        ) : (
          <span></span>
        )}
      </p>
      <p className={styles.outside_benchmark_chevron}>
        {isOutsideBenchmark ? <FaPlay size={'0.6rem'} /> : ''}
      </p>
      <p className="align-center">{multiLineBenchmark(benchmarkText)}</p>
    </>
  );
}
